import React, { useEffect, useState, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthLite } from '@/context/AuthLite';
// import BackBtn from '@/components/BackButton'; // DEAL-STYLE: Removed BackButton (can be restored)
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AppSheet, AppSheetFooterButton } from '@/components/ui/app-sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Upload, Search, Filter, FileText, Trash2, Download, Clock, Globe, Edit, Eye, Plus, MoreVertical, CheckSquare, Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { apiUrl, apiFetch } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { selectionItemOutlineClasses } from '@/lib/selection-item-styles';
import { useIsMobile } from '@/hooks/use-mobile';
import { FabMenu } from "@/components/ui/fab-menu";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import ManualsSidebar from '@/components/manuals/ManualsSidebar';
import { InAppPdfGateBoundary } from '@/components/manuals/InAppPdfGateBoundary';
const ManualPdfViewerDialog = lazy(() => import('@/components/manuals/ManualPdfViewerDialog'));
import { downloadManualFile, isPdfManual } from '@/lib/manual-file-actions';
import { generateThumbnailFromPDF, dataUrlToBlob } from '@/lib/pdf-thumbnail';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectionKeyboardShortcuts } from '@/hooks/use-selection-keyboard-shortcuts';
import { SelectionLiveRegion } from '@/components/ui/selection-live-region';
import { SelectionToolbarLayout } from '@/components/ui/selection-toolbar-layout';
import { hapticTap } from "@/lib/haptics";
import { useLongPress } from "@/hooks/use-long-press";

// Language flag mapping
const LANGUAGE_FLAGS: Record<string, string> = {
  'HU': '🇭🇺',
  'SK': '🇸🇰',
  'CZ': '🇨🇿',
  'EN': '🇬🇧',
  'DE': '🇩🇪',
  'PL': '🇵🇱',
};

const LANGUAGE_NAMES: Record<string, string> = {
  'HU': 'Magyar',
  'SK': 'Slovenský',
  'CZ': 'Český',
  'EN': 'English',
  'DE': 'Deutsch',
  'PL': 'Polski',
};

interface Manual {
  id: string;
  name: string;
  description?: string | null;
  file_name: string;
  file_size?: number | null;
  file_mime?: string | null;
  r2_url: string;
  created_at: string;
  // New hierarchical fields
  manufacturer?: string | null;
  category?: string | null;
  subcategory?: string | null;
  family?: string | null;
  languages?: string[] | null;
  file_type?: string | null; // Legacy, kept for backward compatibility
  file_types?: string[] | null; // New: array of file types
  size_mb?: number | null;
  tags?: ManualTag[];
  thumbnail_url?: string | null;
}

interface ManualTag {
  id: string;
  name: string;
  category?: string | null;
  color?: string | null;
}

interface FilterOptions {
  manufacturers: string[];
  categories: string[];
  families: string[];
  models: string[];
  languages: string[];
  fileTypes: string[];
}

interface FilterState {
  category?: string;
  subcategory?: string;
  manufacturer?: string;
  family?: string;
  manufacturers: Set<string>;
  languages: string[];
}

// Manual Card Component with checkbox functionality
function ManualCard({
  manual,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onPreviewManual,
  onDownloadManual,
  userRole,
  selectionModeActive,
  enableLongPressToSelect,
}: {
  manual: Manual;
  isSelected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onEdit: (manual: Manual) => void;
  onDelete: (manual: Manual) => void;
  onPreviewManual: (manual: Manual) => void;
  onDownloadManual: (manual: Manual) => void;
  userRole?: string;
  selectionModeActive: boolean;
  enableLongPressToSelect: boolean;
}) {
  const { t } = useTranslation();
  const longPress = useLongPress({
    enabled: enableLongPressToSelect,
    onLongPress: () => {
      hapticTap(12);
      onSelect(manual.id, true);
    },
  });
  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
      return;
    }
    if (selectionModeActive) {
      onSelect(manual.id, !isSelected);
      return;
    }
    onPreviewManual(manual);
  };
  
  const handleCheckboxChange = (checked: boolean) => {
    onSelect(manual.id, checked === true);
  };
  
  return (
    <Card 
      className={cn(
        "hover:shadow-md transition-shadow overflow-hidden relative cursor-pointer",
        selectionItemOutlineClasses(selectionModeActive, isSelected),
      )}
      onClick={handleCardClick}
      {...longPress}
    >
      {selectionModeActive ? (
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
      
      {/* Thumbnail - A4 aspect ratio (210:297 ≈ 1:1.414) */}
      {manual.thumbnail_url ? (
        <div className="w-full aspect-[210/297] bg-muted">
          <img 
            src={manual.thumbnail_url.startsWith('/') ? apiUrl(manual.thumbnail_url) : manual.thumbnail_url} 
            alt={manual.file_name}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div className="w-full aspect-[210/297] bg-muted flex items-center justify-center border-b">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      
      <CardContent className="p-3 space-y-2">
        {/* File name */}
        <CardTitle className="text-sm font-medium line-clamp-2" title={manual.file_name}>
          {manual.file_name}
        </CardTitle>
        
        {/* Manufacturer */}
        {manual.manufacturer && (
          <CardDescription className="text-xs line-clamp-1">
            {manual.manufacturer}
          </CardDescription>
        )}
        
        {/* Languages */}
        {manual.languages && manual.languages.length > 0 && (
          <div className="flex items-center gap-1">
            {manual.languages.map(lang => (
              <span key={lang} title={LANGUAGE_NAMES[lang] || lang} className="text-sm">
                {LANGUAGE_FLAGS[lang] || '🌐'}
              </span>
            ))}
          </div>
        )}
        
        {/* File Types */}
        {manual.file_types && manual.file_types.length > 0 && (
          <div className="text-xs text-muted-foreground line-clamp-1" title={manual.file_types.join(', ')}>
            {manual.file_types.join(', ')}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-1 pt-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onPreviewManual(manual);
            }}
            className="h-8 w-8 p-0"
            title={t('manuals.view')}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDownloadManual(manual);
            }}
            className="h-8 w-8 p-0"
            title={t('manuals.download')}
          >
            <Download className="h-4 w-4" />
          </Button>
          {userRole === 'Admin' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-8 w-8 p-0 ml-auto"
                  title={t('manuals.moreOptions')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(manual)}>
                  <Edit className="h-4 w-4 mr-2" />
                  {t('manuals.edit')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onDelete(manual)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('manuals.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Manuals() {
  const { t, i18n } = useTranslation();
  const manualsLang = (i18n.language === 'sk' ? 'sk' : i18n.language === 'en' ? 'en' : 'hu') as 'hu' | 'sk' | 'en';
  const { user } = useAuthLite();
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  const [inAppPdfViewer, setInAppPdfViewer] = useState<{ data: ArrayBuffer; title: string } | null>(null);

  const openManualFile = useCallback(async (manual: Manual) => {
      const previewTab = window.open("about:blank", "_blank");
      try {
        const res = await apiFetch(`/api/manuals/${manual.id}/file`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const pdf = isPdfManual(manual.file_name, manual.file_mime);
        const useInAppPdf =
          pdf &&
          typeof window !== "undefined" &&
          (window.innerWidth < 768 ||
            (window.matchMedia("(pointer: coarse)").matches &&
              Math.min(window.innerWidth, window.innerHeight) < 768));

        if (useInAppPdf) {
          try {
            previewTab?.close();
          } catch {
            /* ignore */
          }
          const buf = await blob.arrayBuffer();
          setInAppPdfViewer({ data: buf.slice(0), title: manual.file_name });
          return;
        }

        const url = URL.createObjectURL(blob);
        if (previewTab && !previewTab.closed) {
          previewTab.location.replace(url);
          setTimeout(() => URL.revokeObjectURL(url), 120_000);
          return;
        }

        try {
          previewTab?.close();
        } catch {
          /* ignore */
        }

        const fallbackTab = window.open(url, "_blank");
        if (!fallbackTab) {
          if (pdf) {
            const buf = await blob.arrayBuffer();
            URL.revokeObjectURL(url);
            setInAppPdfViewer({ data: buf.slice(0), title: manual.file_name });
            return;
          }
          const link = document.createElement("a");
          link.href = url;
          link.download = manual.file_name;
          link.rel = "noopener";
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
          return;
        }

        setTimeout(() => URL.revokeObjectURL(url), 120_000);
      } catch (err) {
        try {
          previewTab?.close();
        } catch {
          /* ignore */
        }
        console.error("[Manuals] Preview failed:", err);
      }
  }, []);

  const handleDownloadManual = useCallback((manual: Manual) => {
    void downloadManualFile(manual.id, manual.file_name, manual.file_mime);
  }, []);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 200); // Debounce search query by 200ms for snappier feel
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filters, setFilters] = useState<FilterState>({
    manufacturers: new Set(),
    languages: [],
  });
  
  // Bulk operations state
  const [selectedManuals, setSelectedManuals] = useState<Set<string>>(new Set());
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    languages: [] as string[],
    category: '',
    subcategory: '',
    manufacturer: '',
  });
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const bulkHeaderDismissRef = useRef<HTMLButtonElement>(null);
  const canBulkSelect = user?.role === 'Admin';
  const selectionMode = canBulkSelect && selectionModeEnabled;
  const desktopSelectionMode = !isMobile && canBulkSelect && selectionModeEnabled;

  const exitManualSelectionMode = useCallback(() => {
    setSelectionModeEnabled(false);
    setSelectedManuals(new Set());
    setShowBulkEditDialog(false);
  }, []);
  
  // Mobil: szűrő panel (Sheet)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<Array<{ id: string; file: File; status: 'queued' | 'uploading' | 'done' | 'error'; name?: string; fileTypes?: string }>>([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadForm, setUploadForm] = useState({
    name: '',
    manufacturer: '',
    category: '',
    subcategory: '',
    languages: [] as string[],
    fileTypes: '', // Free text field for types (comma-separated or line-separated)
  });

  // Dialogs for creating new items
  const [createManufacturerDialogOpen, setCreateManufacturerDialogOpen] = useState(false);
  const [createCategoryDialogOpen, setCreateCategoryDialogOpen] = useState(false);
  const [createSubcategoryDialogOpen, setCreateSubcategoryDialogOpen] = useState(false);
  const [newManufacturerName, setNewManufacturerName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryCode, setNewSubcategoryCode] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingManual, setEditingManual] = useState<Manual | null>(null);
  
  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [manualToDelete, setManualToDelete] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteManyOpen, setBulkDeleteManyOpen] = useState(false);
  const [bulkDeleteManyBusy, setBulkDeleteManyBusy] = useState(false);
  
  const handleDeleteClick = (manual: Manual) => {
    setManualToDelete({ id: manual.id, name: manual.name || manual.file_name });
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    if (manualToDelete) {
      deleteMutation.mutate(manualToDelete.id);
      setDeleteConfirmOpen(false);
      setManualToDelete(null);
    }
  };

  const [editForm, setEditForm] = useState({
    name: '',
    manufacturer: '',
    category: '',
    subcategory: '',
    languages: [] as string[],
    fileTypes: '', // Free text field for types (comma-separated or line-separated)
  });

  // DEAL-STYLE: View mode state (table or cards)
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');

  // Fetch manufacturers
  const manufacturersQuery = useQuery({
    queryKey: ['manual-manufacturers', manualsLang],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/manuals/manufacturers?lang=${manualsLang}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadManufacturers'));
      const data = await res.json();
      return data.manufacturers as Array<{ id: string; name: string; logo_url?: string; display_order: number }>;
    },
  });

  // Fetch categories
  const categoriesQuery = useQuery({
    queryKey: ['manual-categories', manualsLang],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/manuals/categories?lang=${manualsLang}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadCategories'));
      const data = await res.json();
      return data.categories as Array<{ id: string; code: string; name: string; display_order: number }>;
    },
  });

  // Fetch families (filtered by manufacturer + category)
  const familiesQuery = useQuery({
    queryKey: ['manual-families', filters.manufacturer, filters.category, manualsLang],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
      if (filters.category) params.append('category', filters.category);
      params.append('lang', manualsLang);
      
      const res = await fetch(apiUrl(`/api/manuals/families?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadFamilies'));
      const data = await res.json();
      return data.families as Array<{ id: string; manufacturer: string; category: string; family_name: string; display_order: number }>;
    },
    enabled: true, // Always fetch, filtering happens on backend
  });

  // Fetch all subcategories - for table display
  const allSubcategoriesQuery = useQuery({
    queryKey: ['manual-subcategories-all', manualsLang],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/manuals/subcategories?lang=${manualsLang}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadSubcategories'));
      const data = await res.json();
      return data.subcategories as Array<{ id: string; category_id: string; code: string; name: string; display_order: number }>;
    },
  });

  // Fetch subcategories (filtered by category) - for filters
  const subcategoriesFilterQuery = useQuery({
    queryKey: ['manual-subcategories-filter', filters.category],
    queryFn: async () => {
      // Get category ID from code
      const category = categoriesQuery.data?.find(c => c.code === filters.category);
      if (!category) return [];
      
      const params = new URLSearchParams();
      params.append('categoryId', category.id);
      
      const res = await fetch(apiUrl(`/api/manuals/subcategories?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadSubcategories'));
      const data = await res.json();
      return data.subcategories as Array<{ id: string; category_id: string; code: string; name: string; display_order: number }>;
    },
    enabled: !!filters.category,
  });

  // Fetch subcategories (filtered by category) - for edit dialog
  const subcategoriesQuery = useQuery({
    queryKey: ['manual-subcategories-edit', editForm.category],
    queryFn: async () => {
      // Get category ID from code
      const category = categoriesQuery.data?.find(c => c.code === editForm.category);
      if (!category) return [];
      
      const params = new URLSearchParams();
      params.append('categoryId', category.id);
      
      const res = await fetch(apiUrl(`/api/manuals/subcategories?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadSubcategories'));
      const data = await res.json();
      return data.subcategories as Array<{ id: string; category_id: string; code: string; name: string; display_order: number }>;
    },
    enabled: !!editForm.category && editDialogOpen,
  });

  // Fetch subcategories (filtered by category) - for upload dialog
  const subcategoriesUploadQuery = useQuery({
    queryKey: ['manual-subcategories-upload', uploadForm.category],
    queryFn: async () => {
      // Get category ID from code
      const category = categoriesQuery.data?.find(c => c.code === uploadForm.category);
      if (!category) return [];
      
      const params = new URLSearchParams();
      params.append('categoryId', category.id);
      
      const res = await fetch(apiUrl(`/api/manuals/subcategories?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadSubcategories'));
      const data = await res.json();
      return data.subcategories as Array<{ id: string; category_id: string; code: string; name: string; display_order: number }>;
    },
    enabled: !!uploadForm.category && !!categoriesQuery.data,
  });

  // Fetch manuals with hierarchical filters and pagination
  const manualsQuery = useQuery({
    queryKey: ['manuals', {
      ...filters,
      manufacturers: Array.from(filters.manufacturers).sort(),
    }, debouncedSearchQuery, currentPage],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Hierarchical filters
      if (filters.category) params.append('category', filters.category);
      if (filters.subcategory) params.append('subcategory', filters.subcategory);
      // Multiple manufacturers (OR logic within group, AND with other filters)
      if (filters.manufacturers.size > 0) {
        Array.from(filters.manufacturers).forEach(mfg => {
          params.append('manufacturer', mfg);
        });
      }
      if (filters.languages.length > 0) params.append('languages', filters.languages.join(','));
      if (debouncedSearchQuery) params.append('search', debouncedSearchQuery);
      
      // Pagination
      const offset = (currentPage - 1) * itemsPerPage;
      params.append('limit', String(itemsPerPage));
      params.append('offset', String(offset));
      
      const res = await fetch(apiUrl(`/api/manuals?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadManuals'));
      const data = await res.json();
      return {
        manuals: data.manuals as Manual[],
        total: data.total as number,
        page: data.page as number,
        limit: data.limit as number,
      };
    },
  });

  const visibleManuals = manualsQuery.data?.manuals ?? [];
  const allVisibleSelected =
    visibleManuals.length > 0 && visibleManuals.every((m) => selectedManuals.has(m.id));
  const someVisibleSelected = visibleManuals.some((m) => selectedManuals.has(m.id));

  useSelectionKeyboardShortcuts({
    enabled: selectionMode,
    onExit: exitManualSelectionMode,
    onSelectAllInView: () => {
      if (visibleManuals.length === 0) return;
      setSelectedManuals(new Set(visibleManuals.map((m) => m.id)));
    },
  });

  useEffect(() => {
    if (!selectionMode) return;
    const id = requestAnimationFrame(() => bulkHeaderDismissRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectionMode]);

  // Fetch filter options (hierarchical)
  const filterOptionsQuery = useQuery({
    queryKey: ['manual-filter-options', filters.manufacturer, filters.category, filters.family],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.manufacturer) params.append('manufacturer', filters.manufacturer);
      if (filters.category) params.append('category', filters.category);
      if (filters.family) params.append('family', filters.family);
      
      const res = await fetch(apiUrl(`/api/manuals/filter-options?${params.toString()}`), {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorLoadFilterOptions'));
      const data = await res.json();
      return data.options as FilterOptions;
    },
  });

  // Fetch search history
  const searchHistoryQuery = useQuery({
    queryKey: ['manual-search-history'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/api/manuals/search-history?limit=10'), {
        credentials: 'include',
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.history as string[];
    },
  });

  // Save search history mutation
  const saveSearchHistoryMutation = useMutation({
    mutationFn: async (term: string) => {
      const res = await fetch(apiUrl('/api/manuals/search-history'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ searchTerm: term }),
      });
      if (!res.ok) throw new Error(t('manuals.errorSaveSearchHistory'));
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-search-history'] });
    },
  });

  // Computed filter options based on active filters
  const availableOptions = useMemo(() => {
    // Default languages if none are available from the database
    const DEFAULT_LANGUAGES = ['HU', 'SK', 'CZ', 'EN', 'DE', 'PL'];
    
    const options = filterOptionsQuery.data || {
      manufacturers: [],
      categories: [],
      families: [],
      models: [],
      languages: [],
      fileTypes: [],
    };
    
    // Use default languages if no languages are available from the database
    const availableLanguages = options.languages && options.languages.length > 0 
      ? options.languages 
      : DEFAULT_LANGUAGES;
    
    // Use hierarchical data from queries
    const manufacturers = manufacturersQuery.data?.map(m => m.name) || options.manufacturers;
    const categories = categoriesQuery.data?.map(c => ({ code: c.code, name: c.name })) || options.categories.map(c => ({ code: c, name: c }));
    const families = familiesQuery.data?.map(f => f.family_name) || options.families;
    
    return {
      manufacturers: [...new Set(manufacturers)].sort(),
      categories: categories.map(c => typeof c === 'string' ? c : c.code),
      categoryNames: categories.reduce((acc, c) => {
        const code = typeof c === 'string' ? c : c.code;
        const name = typeof c === 'string' ? c : c.name;
        acc[code] = name;
        return acc;
      }, {} as Record<string, string>),
      families: [...new Set(families)].sort(),
      languages: availableLanguages,
      fileTypes: options.fileTypes,
    };
  }, [filterOptionsQuery.data, manufacturersQuery.data, categoriesQuery.data, familiesQuery.data, filters]);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
  }, [qc]);

  // Handle search
  const handleSearch = useCallback((term: string) => {
    setSearchQuery(term);
    setCurrentPage(1); // Reset to first page when search changes
    if (term.trim()) {
      saveSearchHistoryMutation.mutate(term);
    }
  }, [saveSearchHistoryMutation]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      
      // Generate thumbnails for PDF files and add them to formData
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        formData.append('files', file);
        
        // Add file-specific name and types if provided
        const queueItem = uploadQueue.find(item => item.file === file);
        if (queueItem?.name) {
          formData.append(`name_${i}`, queueItem.name);
        }
        if (queueItem?.fileTypes) {
          // Convert comma or line-separated string to array
          const typesArray = queueItem.fileTypes
            .split(/[,\n]/)
            .map(t => t.trim())
            .filter(t => t.length > 0);
          if (typesArray.length > 0) {
            formData.append(`fileTypes_${i}`, JSON.stringify(typesArray));
          }
        }
        
        // Generate thumbnail for PDF files
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          try {
            console.log(`[Manuals Upload] Generating thumbnail for ${file.name}...`);
            const thumbnailDataUrl = await generateThumbnailFromPDF(file, {
              width: 300,
              height: 400,
              scale: 1.5,
            });
            
            if (thumbnailDataUrl) {
              // Convert data URL to blob and append to formData
              const thumbnailBlob = dataUrlToBlob(thumbnailDataUrl);
              formData.append(`thumbnail_${file.name}`, thumbnailBlob, `${file.name.replace(/\.pdf$/i, '')}_thumbnail.png`);
              console.log(`[Manuals Upload] Thumbnail generated for ${file.name}`);
            } else {
              console.warn(`[Manuals Upload] Failed to generate thumbnail for ${file.name}`);
            }
          } catch (error) {
            console.error(`[Manuals Upload] Error generating thumbnail for ${file.name}:`, error);
            // Continue with upload even if thumbnail generation fails
          }
        }
      }
      
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }
      // Add global metadata fields (fallback if no file-specific name)
      if (uploadForm.name) {
        formData.append('name', uploadForm.name);
      }
      if (uploadForm.manufacturer) {
        formData.append('manufacturer', uploadForm.manufacturer);
      }
      if (uploadForm.category) {
        formData.append('category', uploadForm.category);
      }
      if (uploadForm.subcategory) {
        formData.append('subcategory', uploadForm.subcategory);
      }
      if (uploadForm.languages.length > 0) {
        formData.append('languages', JSON.stringify(uploadForm.languages));
      }

      const res = await fetch(apiUrl('/api/manuals/upload'), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch {
          errorData = { message: `HTTP ${res.status}: ${res.statusText}` };
        }
        console.error('[Manuals Upload] API Error:', errorData);
        throw new Error(errorData.message || errorData.error || t('manuals.errorUpload'));
      }

      const result = await res.json();
      console.log('[Manuals Upload] Success:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('[Manuals Upload] Mutation success:', data);
      if (data.errors && data.errors.length > 0) {
        console.warn('[Manuals Upload] Some files failed:', data.errors);
        const errorMessages = data.errors.map((e: any) => {
          const errorText = typeof e.error === 'string' 
            ? e.error 
            : (e.error?.message || e.error?.error || JSON.stringify(e.error));
          return `${e.file}: ${errorText}`;
        }).join('\n');
        alert(`${t('manuals.errorUploadPartial')}\n${errorMessages}`);
      }
      qc.invalidateQueries({ queryKey: ['manuals'] });
      qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
      setUploadQueue([]);
      setUploadDescription('');
      setUploadForm({
        name: '',
        manufacturer: '',
        category: '',
        subcategory: '',
        languages: [],
        fileTypes: '',
      });
      setUploadDialogOpen(false);
    },
    onError: (error) => {
      console.error('[Manuals Upload] Mutation error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (typeof error === 'string' ? error : JSON.stringify(error));
      alert(`${t('manuals.errorUpload')}: ${errorMessage}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await fetch(apiUrl(`/api/manuals/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || t('manualsExtra.updateFailed'));
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manuals'] });
      qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
      setEditDialogOpen(false);
      setEditingManual(null);
    },
    onError: (error) => {
      alert(`${t("common.updateError")}: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(apiUrl(`/api/manuals/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(t('manuals.errorDelete'));
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manuals'] });
      qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
      setDeleteConfirmOpen(false);
      setManualToDelete(null);
    },
  });

  const confirmBulkDeleteManuals = useCallback(async () => {
    const ids = Array.from(selectedManuals);
    if (ids.length === 0) return;
    setBulkDeleteManyBusy(true);
    try {
      for (const id of ids) {
        await deleteMutation.mutateAsync(id);
      }
      setBulkDeleteManyOpen(false);
      exitManualSelectionMode();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkDeleteManyBusy(false);
    }
  }, [selectedManuals, deleteMutation, exitManualSelectionMode]);

  // Create manufacturer mutation
  const createManufacturerMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(apiUrl('/api/manuals/manufacturers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || t('manuals.errorCreateManufacturer'));
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['manual-manufacturers'] });
      qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
      const newName = newManufacturerName.trim();
      setNewManufacturerName('');
      setCreateManufacturerDialogOpen(false);
      // Set the newly created manufacturer as selected (in upload or edit form)
      const manufacturerName = data?.manufacturer?.name || newName;
      if (manufacturerName) {
        if (editDialogOpen && editingManual) {
          setEditForm(prev => ({ ...prev, manufacturer: manufacturerName }));
        } else {
          setUploadForm(prev => ({ ...prev, manufacturer: manufacturerName }));
        }
      }
    },
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async ({ code, name }: { code: string; name: string }) => {
      const res = await fetch(apiUrl('/api/manuals/categories'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, name }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || t('manuals.errorCreateCategory'));
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['manual-categories'] });
      qc.invalidateQueries({ queryKey: ['manual-filter-options'] });
      const newCode = newCategoryCode.trim();
      setNewCategoryCode('');
      setNewCategoryName('');
      setCreateCategoryDialogOpen(false);
      // Set the newly created category as selected (in upload or edit form)
      const categoryCode = data?.category?.code || newCode;
      if (categoryCode) {
        if (editDialogOpen && editingManual) {
          setEditForm(prev => ({ ...prev, category: categoryCode, subcategory: '' }));
        } else {
          setUploadForm(prev => ({ ...prev, category: categoryCode, subcategory: '' }));
        }
      }
    },
    onError: (error) => {
      console.error('[Manuals] Create category error:', error);
      alert(t('manuals.errorCreateCategoryAlert', { message: error instanceof Error ? error.message : String(error) }));
    },
  });

  // Create subcategory mutation
  const createSubcategoryMutation = useMutation({
    mutationFn: async ({ code, name }: { code: string; name: string }) => {
      // Get category from either upload form or edit form
      const categoryCode = editDialogOpen && editingManual ? editForm.category : uploadForm.category;
      const category = categoriesQuery.data?.find(c => c.code === categoryCode);
      if (!category) throw new Error(t('manualsExtra.selectCategoryFirst'));
      
      const res = await fetch(apiUrl('/api/manuals/subcategories'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ category_id: category.id, code, name }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || t('manualsExtra.createSubcategoryFailed'));
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['manual-subcategories-upload'] });
      qc.invalidateQueries({ queryKey: ['manual-subcategories-edit'] });
      const newCode = newSubcategoryCode.trim();
      setNewSubcategoryCode('');
      setNewSubcategoryName('');
      setCreateSubcategoryDialogOpen(false);
      // Set the newly created subcategory as selected (in upload or edit form)
      const subcategoryCode = data?.subcategory?.code || newCode;
      if (subcategoryCode) {
        if (editDialogOpen && editingManual) {
          setEditForm(prev => ({ ...prev, subcategory: subcategoryCode }));
        } else {
          setUploadForm(prev => ({ ...prev, subcategory: subcategoryCode }));
        }
      }
    },
  });

  // Open edit dialog
  const handleEdit = (manual: Manual) => {
    setEditingManual(manual);
    setEditForm({
      name: manual.name || '',
      manufacturer: manual.manufacturer || '',
      category: manual.category || '',
      subcategory: manual.subcategory || '',
      languages: manual.languages || [],
      fileTypes: manual.file_types ? manual.file_types.join(', ') : (manual.file_type || ''), // Convert array to comma-separated string
    });
    setEditDialogOpen(true);
  };

  // Save edit
  const handleSaveEdit = () => {
    if (!editingManual) return;
    // Convert fileTypes string to array
    const fileTypesArray = editForm.fileTypes
      .split(/[,\n]/)
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    updateMutation.mutate({
      id: editingManual.id,
      data: {
        ...editForm,
        fileTypes: fileTypesArray,
      },
    });
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const items = Array.from(files).map(file => ({
      id: `${file.name}_${file.size}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      file,
      status: 'queued' as const,
    }));
    setUploadQueue(prev => [...prev, ...items]);
  };

  const handleUpload = async () => {
    if (uploadQueue.length === 0) return;
    const files = uploadQueue.map(item => item.file);
    
    setUploadQueue(prev => prev.map(item => ({ ...item, status: 'uploading' as const })));
    
    try {
      await uploadMutation.mutateAsync(files);
    } catch (error) {
      setUploadQueue(prev => prev.map(item => ({ ...item, status: 'error' as const })));
    }
  };
  
  // Update file name in queue
  const updateFileName = (id: string, name: string) => {
    setUploadQueue(prev => prev.map(item => 
      item.id === id ? { ...item, name } : item
    ));
  };

  const updateFileTypes = (id: string, fileTypes: string) => {
    setUploadQueue(prev => prev.map(item => 
      item.id === id ? { ...item, fileTypes } : item
    ));
  };

  const removeFromQueue = (id: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  // Category counts query - fetch all manuals per category for counts
  const categoryCountsQuery = useQuery({
    queryKey: ['manual-category-counts'],
    queryFn: async () => {
      // Fetch all manuals without pagination to get counts
      const res = await fetch(apiUrl('/api/manuals?limit=10000&offset=0'), {
        credentials: 'include',
      });
      if (!res.ok) return {};
      const data = await res.json();
      const manuals = data.manuals as Manual[];
      const counts: Record<string, number> = {};
      manuals.forEach(manual => {
        if (manual.category) {
          counts[manual.category] = (counts[manual.category] || 0) + 1;
        }
      });
      return counts;
    },
  });

  const totalPages = Math.ceil((manualsQuery.data?.total || 0) / itemsPerPage);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header: always visible title; view toggle stays below */}
      <div className="relative z-20 h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        {/* Normal Header — 60px title row; view toggle stays in content below */}
        <div
          className={cn(
            "absolute inset-0 flex items-center transition-all duration-300 ease-in-out",
            desktopSelectionMode
              ? "opacity-0 translate-y-[-100%] pointer-events-none"
              : "opacity-100 translate-y-0"
          )}
        >
          {/* btn-token: unified D */}
          <PageTitleBar
            className="w-full border-0"
            title={t("manuals.title")}
            right={
              isMobile ? (
                <div className="flex shrink-0 items-center gap-2">
                  {canBulkSelect ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 shrink-0 p-0"
                      type="button"
                      aria-label={selectionModeEnabled ? t("manuals.selectionOff") : t("manuals.selection")}
                      onClick={() => {
                        hapticTap(12);
                        if (selectionModeEnabled) exitManualSelectionMode();
                        else setSelectionModeEnabled(true);
                      }}
                    >
                      {selectionModeEnabled ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    onClick={() => setFilterSheetOpen(true)}
                    aria-label={t("common.filterLabel")}
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                </div>
              ) : canBulkSelect ? (
                <div className="flex items-center gap-1 md:gap-2">
                  {!selectionModeEnabled ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="px-2 text-xs md:px-3 md:text-sm"
                        type="button"
                        onClick={() => setSelectionModeEnabled(true)}
                      >
                        {t("common.selectTrigger")}
                      </Button>
                      <Button size="sm" type="button" onClick={() => setUploadDialogOpen(true)}>
                        <Upload className="h-4 w-4 mr-2" />
                        {t("manuals.upload")}
                      </Button>
                    </>
                  ) : null}
                </div>
              ) : undefined
            }
          />
        </div>

        {/* Desktop Bulk Actions Toolbar - slides up when selection mode is on (Admin) */}
        {!isMobile ? (
          <div
            className={cn(
              "absolute inset-0 flex items-center justify-between px-3 transition-all duration-300 ease-in-out sm:px-4",
              desktopSelectionMode ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[100%] pointer-events-none",
            )}
          >
            <SelectionToolbarLayout
              closeButtonRef={bulkHeaderDismissRef}
              className="px-0 sm:px-0"
              selectedCount={selectedManuals.size}
              onClose={() => {
                hapticTap(12);
                exitManualSelectionMode();
              }}
              onSelectAll={() => setSelectedManuals(new Set(visibleManuals.map((m) => m.id)))}
              selectAllDisabled={visibleManuals.length === 0}
              bulkActions={
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedManuals.size === 0}
                  onClick={() => setShowBulkEditDialog(true)}
                >
                  {t("manuals.editBulk")}
                </Button>
              }
              onDelete={() => setBulkDeleteManyOpen(true)}
              deleteDisabled={selectedManuals.size === 0}
              deletePending={bulkDeleteManyBusy || deleteMutation.isPending}
            />
          </div>
        ) : null}
      </div>

      {/* Content with Sidebar */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[360px,1fr] gap-3 md:gap-6 h-full">
          {/* Sidebar */}
          <div className="hidden md:block overflow-y-auto">
            <div className="p-4">
              <ManualsSidebar
                searchQuery={searchQuery}
                onSearchChange={handleSearch}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                searchHistory={searchHistoryQuery.data}
                onSearchHistorySelect={handleSearch}
                categories={categoriesQuery.data || []}
                subcategories={allSubcategoriesQuery.data || []}
                manufacturers={manufacturersQuery.data || []}
                languages={availableOptions.languages}
                categoryCounts={categoryCountsQuery.data || {}}
                isLoadingCategories={categoriesQuery.isLoading}
                isLoadingManufacturers={manufacturersQuery.isLoading}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="container mx-auto py-3 md:py-6 px-2 md:px-4 space-y-4 md:space-y-6">
              {/* Manuals List */}
          {manualsQuery.isLoading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("common.loading")}</p>
            </div>
          )}

          {manualsQuery.error && (
            <div className="text-center py-12">
              <p className="text-destructive">
                {t("common.error")}: {manualsQuery.error instanceof Error ? manualsQuery.error.message : t("common.error")}
              </p>
            </div>
          )}

          {manualsQuery.data && (
            <>
              {/* One row: Karty / Tabuľka + Filter (same as Obrázky page) */}
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'table' | 'cards')} className="w-full">
                <div className={cn("mb-3 mt-0 flex min-w-0 items-center gap-2", isMobile && "flex-nowrap")}>
                  <TabsList
                    className={cn(
                      "inline-flex w-full sm:w-auto",
                      isMobile && "h-8 min-w-0 flex-1 shrink",
                    )}
                  >
                    <TabsTrigger value="cards" className={cn(isMobile && "px-2 text-xs")}>
                      {t("manuals.cardsView")}
                    </TabsTrigger>
                    <TabsTrigger value="table" className={cn(isMobile && "px-2 text-xs")}>
                      {t("manuals.tableView")}
                    </TabsTrigger>
                  </TabsList>
                </div>

                {manualsQuery.data.manuals.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">{t('manuals.noResults')}</p>
                  </div>
                ) : (
                <>
                    <TabsContent value="table" className="mt-0">
                      <div className="border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <Table>
                      <TableHeader>
                        <TableRow>
                          {selectionMode ? (
                            <TableHead className="w-[44px]">
                              <Checkbox
                                aria-label={t("common.selectAll")}
                                checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                                onCheckedChange={(v) => {
                                  if (v === true) setSelectedManuals(new Set(visibleManuals.map((m) => m.id)));
                                  else setSelectedManuals(new Set());
                                }}
                              />
                            </TableHead>
                          ) : null}
                          <TableHead className="w-[120px]">{t('manuals.manufacturer')}</TableHead>
                          <TableHead className="w-[150px]">{t('manuals.category')}</TableHead>
                          <TableHead className="w-[150px]">{t('manualsExtra.subcategoryHead')}</TableHead>
                          <TableHead className="min-w-[200px]">{t('manualsExtra.fileNameHead')}</TableHead>
                          <TableHead className="w-[100px]">{t('manualsExtra.languagesHead')}</TableHead>
                          <TableHead className="w-[200px]">{t('manualsExtra.typesHead')}</TableHead>
                          <TableHead className="w-[80px]">{t('manualsExtra.sizeHead')}</TableHead>
                          <TableHead className="w-[150px]">{t('manualsExtra.actionsHead')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {manualsQuery.data.manuals.map((manual) => (
                    <TableRow
                      key={manual.id}
                      className={cn(
                        selectionMode && "cursor-pointer",
                        selectionItemOutlineClasses(
                          selectionMode,
                          selectedManuals.has(manual.id),
                          { denseSelectedBg: true },
                        ),
                      )}
                      onClick={() => {
                        if (!selectionMode) return;
                        setSelectedManuals((prev) => {
                          const next = new Set(prev);
                          if (next.has(manual.id)) next.delete(manual.id);
                          else next.add(manual.id);
                          return next;
                        });
                      }}
                    >
                      {selectionMode ? (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedManuals.has(manual.id)}
                            onCheckedChange={(v) => {
                              setSelectedManuals((prev) => {
                                const next = new Set(prev);
                                if (v === true) next.add(manual.id);
                                else next.delete(manual.id);
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="font-medium">
                        {manual.manufacturer || '-'}
                      </TableCell>
                      <TableCell>
                        {manual.category ? (
                          <span className="text-sm">
                            {availableOptions.categoryNames?.[manual.category] || manual.category}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {manual.subcategory ? (
                          <span className="text-sm">
                            {(() => {
                              const category = categoriesQuery.data?.find(c => c.code === manual.category);
                              if (!category) {
                                const sub = allSubcategoriesQuery.data?.find(s => s.code === manual.subcategory);
                                return sub?.name || manual.subcategory;
                              }
                              const sub = allSubcategoriesQuery.data?.find(s => s.category_id === category.id && s.code === manual.subcategory);
                              return sub?.name || manual.subcategory;
                            })()}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate max-w-[200px]" title={manual.file_name}>
                            {manual.file_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {manual.languages && manual.languages.length > 0 ? (
                            manual.languages.map(lang => (
                              <span key={lang} title={LANGUAGE_NAMES[lang] || lang}>
                                {LANGUAGE_FLAGS[lang] || '🌐'}
                              </span>
                            ))
                          ) : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {manual.file_types && manual.file_types.length > 0 ? (
                          <span className="text-sm text-muted-foreground" title={manual.file_types.join(', ')}>
                            {manual.file_types.join(', ')}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {manual.size_mb ? `${manual.size_mb.toFixed(2)} MB` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openManualFile(manual);
                            }}
                            title={t('manuals.view')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadManual(manual);
                            }}
                            title={t('manuals.download')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {user?.role === 'Admin' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title={t("manuals.actionsLabel")}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    handleEdit(manual);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Szerkesztés
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    handleDeleteClick(manual);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Törlés
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="cards" className="mt-0">
                      {/* DEAL-STYLE: Card view (can be removed: entire TabsContent) */}
                      <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5")}>
                        {manualsQuery.data.manuals.map((manual) => (
                          <ManualCard
                            key={manual.id}
                            manual={manual}
                            isSelected={selectedManuals.has(manual.id)}
                            selectionModeActive={selectionMode}
                            enableLongPressToSelect={Boolean(isMobile && canBulkSelect && !selectionModeEnabled)}
                            onSelect={(id, checked) => {
                              setSelectedManuals(prev => {
                                const next = new Set(prev);
                                if (checked) {
                                  next.add(id);
                                } else {
                                  next.delete(id);
                                }
                                return next;
                              });
                            }}
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                            onPreviewManual={openManualFile}
                            onDownloadManual={handleDownloadManual}
                            userRole={user?.role}
                          />
                        ))}
                      </div>
                    </TabsContent>
                </>
                )}
                  </Tabs>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center pt-4">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 1) setCurrentPage(currentPage - 1);
                              }}
                              className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                          
                          {/* Page numbers */}
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <PaginationItem key={pageNum}>
                                <PaginationLink
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentPage(pageNum);
                                  }}
                                  isActive={currentPage === pageNum}
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}
                          
                          {totalPages > 5 && currentPage < totalPages - 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}

                          <PaginationItem>
                            <PaginationNext
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                              }}
                              className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('manualsExtra.editManualTitle')}</DialogTitle>
          </DialogHeader>
          {editingManual && (
            <div className="space-y-4">
              <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                <div>
                  <Label>Név</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t("manuals.manualNamePlaceholder")}
                  />
                </div>
                <div>
                  <Label>{t('manuals.manufacturer')}</Label>
                  <Select
                    value={editForm.manufacturer}
                    onValueChange={(value) => {
                      if (value === '__create_new__') {
                        setCreateManufacturerDialogOpen(true);
                      } else {
                        setEditForm(prev => ({ ...prev, manufacturer: value }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("manuals.selectManufacturerPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOptions.manufacturers.map(mfg => (
                        <SelectItem key={mfg} value={mfg}>{mfg}</SelectItem>
                      ))}
                      <SelectItem 
                        value="__create_new__" 
                        className="text-primary font-medium"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        {t('manualsExtra.addManufacturerTitle')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('manuals.category')}</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(value) => {
                      if (value === '__create_new__') {
                        setCreateCategoryDialogOpen(true);
                      } else {
                        setEditForm(prev => ({ ...prev, category: value, subcategory: '' })); // Reset subcategory when category changes
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("manuals.selectCategoryPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriesQuery.data?.map(cat => (
                        <SelectItem key={cat.id} value={cat.code}>
                          {cat.name} {/* Magyar név jelenik meg */}
                        </SelectItem>
                      ))}
                      <SelectItem 
                        value="__create_new__" 
                        className="text-primary font-medium"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        {t('manualsExtra.addCategoryTitle')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('manualsExtra.subcategoryLabel')}</Label>
                  <Select
                    value={editForm.subcategory}
                    onValueChange={(value) => {
                      if (value === '__create_new__') {
                        setCreateSubcategoryDialogOpen(true);
                      } else {
                        setEditForm(prev => ({ ...prev, subcategory: value }));
                      }
                    }}
                    disabled={!editForm.category}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={editForm.category ? t('manualsExtra.selectSubcategoryPlaceholder') : t('manualsExtra.selectCategoryFirst')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const category = categoriesQuery.data?.find(c => c.code === editForm.category);
                        if (!category) return null;
                        // Fetch subcategories for this category
                        const subs = subcategoriesQuery.data?.filter(s => s.category_id === category.id) || [];
                        return (
                          <>
                            {subs.map(sub => (
                              <SelectItem key={sub.id} value={sub.code}>
                                {sub.name}
                              </SelectItem>
                            ))}
                            <SelectItem 
                              value="__create_new__" 
                              className="text-primary font-medium"
                            >
                              <Plus className="h-4 w-4 inline mr-2" />
                              {t('manualsExtra.addSubcategoryTitle')}
                            </SelectItem>
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t('manualsExtra.languagesLabel')}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {availableOptions.languages.map(lang => {
                    const isSelected = editForm.languages.includes(lang);
                    return (
                      <button
                        key={lang}
                        onClick={() => {
                          const newLangs = isSelected
                            ? editForm.languages.filter(l => l !== lang)
                            : [...editForm.languages, lang];
                          setEditForm(prev => ({ ...prev, languages: newLangs }));
                        }}
                        className={cn(
                          "px-3 py-1 rounded-md border text-sm flex items-center gap-2",
                          isSelected ? "bg-primary text-primary-foreground" : "bg-background"
                        )}
                      >
                        <span>{LANGUAGE_FLAGS[lang] || '🌐'}</span>
                        <span>{LANGUAGE_NAMES[lang] || lang}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>{t('manualsExtra.typesHead')}</Label>
                <Textarea
                  value={editForm.fileTypes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, fileTypes: e.target.value }))}
                  placeholder={t("manuals.typesExamplePlaceholder")}
                  className="mt-1 min-h-[80px]"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("manualsExtra.enterTypesHint")}
                    </p>
                  </div>


              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditDialogOpen(false);
                    setEditingManual(null);
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? t("common.saving") : t("manualsExtra.saveButton")}
                </Button>
              </div>
            </div>
      )}
        </DialogContent>
      </Dialog>

      {/* Create Manufacturer Dialog */}
      <Dialog open={createManufacturerDialogOpen} onOpenChange={setCreateManufacturerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('manualsExtra.addManufacturerTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Név</Label>
              <Input
                value={newManufacturerName}
                onChange={(e) => setNewManufacturerName(e.target.value)}
                placeholder={t('manuals.manufacturerPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newManufacturerName.trim()) {
                    createManufacturerMutation.mutate(newManufacturerName.trim());
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateManufacturerDialogOpen(false);
                  setNewManufacturerName('');
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  if (newManufacturerName.trim()) {
                    createManufacturerMutation.mutate(newManufacturerName.trim());
                  }
                }}
                disabled={!newManufacturerName.trim() || createManufacturerMutation.isPending}
              >
                {createManufacturerMutation.isPending ? 'Hozzáadás...' : 'Hozzáadás'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={createCategoryDialogOpen} onOpenChange={setCreateCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('manualsExtra.addCategoryTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kód</Label>
              <Input
                value={newCategoryCode}
                onChange={(e) => setNewCategoryCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder={t("manuals.categoryCodePlaceholder")}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('manualsExtra.lowercaseHint')}</p>
            </div>
            <div>
              <Label>Név</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t('manuals.categoryPlaceholder')}
              />
            </div>
            {createCategoryMutation.isError && (
              <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
                {createCategoryMutation.error?.message || t("manualsExtra.categoryCreateFailed")}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateCategoryDialogOpen(false);
                  setNewCategoryCode('');
                  setNewCategoryName('');
                  createCategoryMutation.reset();
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  if (newCategoryCode.trim() && newCategoryName.trim()) {
                    createCategoryMutation.mutate({
                      code: newCategoryCode.trim(),
                      name: newCategoryName.trim(),
                    });
                  }
                }}
                disabled={!newCategoryCode.trim() || !newCategoryName.trim() || createCategoryMutation.isPending}
              >
                {createCategoryMutation.isPending ? 'Hozzáadás...' : 'Hozzáadás'}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground border-t pt-2">
              <p>💡 {t('manuals.tipSettings')}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Subcategory Dialog */}
      <Dialog open={createSubcategoryDialogOpen} onOpenChange={setCreateSubcategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('manualsExtra.addSubcategoryTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!uploadForm.category && (
              <div className="text-sm text-muted-foreground">
                {t('manualsExtra.selectCategoryFirstInForm')}
              </div>
            )}
            <div>
              <Label>Kód</Label>
              <Input
                value={newSubcategoryCode}
                onChange={(e) => setNewSubcategoryCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder={t("manuals.subcategoryCodePlaceholder")}
                disabled={!uploadForm.category}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('manualsExtra.lowercaseHint')}</p>
            </div>
            <div>
              <Label>Név</Label>
              <Input
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                placeholder={t("manuals.subcategoryNamePlaceholder")}
                disabled={!uploadForm.category}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateSubcategoryDialogOpen(false);
                  setNewSubcategoryCode('');
                  setNewSubcategoryName('');
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  if (newSubcategoryCode.trim() && newSubcategoryName.trim() && uploadForm.category) {
                    createSubcategoryMutation.mutate({
                      code: newSubcategoryCode.trim(),
                      name: newSubcategoryName.trim(),
                    });
                    // After successful creation, set the new subcategory as selected
                    setTimeout(() => {
                      setUploadForm(prev => ({ ...prev, subcategory: newSubcategoryCode.trim() }));
                    }, 100);
                  }
                }}
                disabled={!newSubcategoryCode.trim() || !newSubcategoryName.trim() || !uploadForm.category || createSubcategoryMutation.isPending}
              >
                {createSubcategoryMutation.isPending ? 'Hozzáadás...' : 'Hozzáadás'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('manualsExtra.deleteManualTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              Biztosan törölni szeretnéd a <strong>"{manualToDelete?.name}"</strong> manuált?
              <br />
              <br />
              {t('manualsExtra.deleteManualConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteConfirmOpen(false);
              setManualToDelete(null);
            }}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('manuals.deleting') : t('manuals.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteManyOpen} onOpenChange={setBulkDeleteManyOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("manualsExtra.bulkDeleteManyTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("manualsExtra.bulkDeleteManyDescription", { count: selectedManuals.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteManyBusy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleteManyBusy || selectedManuals.size === 0}
              onClick={(e) => {
                e.preventDefault();
                void confirmBulkDeleteManuals();
              }}
            >
              {bulkDeleteManyBusy ? t("manuals.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('manualsExtra.bulkEditTitle', { count: selectedManuals.size })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('manualsExtra.addLanguagesLabel')}</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {t("manualsExtra.selectLanguagesPrompt")}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {availableOptions.languages.map(lang => {
                  const isSelected = bulkEditForm.languages.includes(lang);
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => {
                        setBulkEditForm(prev => ({
                          ...prev,
                          languages: isSelected
                            ? prev.languages.filter(l => l !== lang)
                            : [...prev.languages, lang]
                        }));
                      }}
                      className={cn(
                        "px-3 py-1 rounded-md text-sm border transition-colors",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "hover:bg-accent"
                      )}
                    >
                      {LANGUAGE_FLAGS[lang] || '🌐'} {LANGUAGE_NAMES[lang] || lang}
                    </button>
                  );
                })}
        </div>
      </div>
            <div>
              <Label>{t('manuals.category')}</Label>
              <Select
                value={bulkEditForm.category || undefined}
                onValueChange={(value) => {
                  setBulkEditForm(prev => ({ ...prev, category: value || '', subcategory: '' }));
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("manuals.selectCategoryOptionalPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availableOptions.categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {availableOptions.categoryNames?.[cat] || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bulkEditForm.category && (
              <div>
                <Label>{t('manualsExtra.subcategoryLabel')}</Label>
                <Select
                  value={bulkEditForm.subcategory || undefined}
                  onValueChange={(value) => {
                    setBulkEditForm(prev => ({ ...prev, subcategory: value || '' }));
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t("manuals.selectSubcategoryOptionalPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoriesFilterQuery.data
                      ?.filter(sub => {
                        const category = categoriesQuery.data?.find(c => c.code === bulkEditForm.category);
                        return category && sub.category_id === category.id;
                      })
                      .map(sub => (
                        <SelectItem key={sub.id} value={sub.code}>
                          {sub.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t('manuals.manufacturer')}</Label>
              <Select
                value={bulkEditForm.manufacturer || undefined}
                onValueChange={(value) => {
                  setBulkEditForm(prev => ({ ...prev, manufacturer: value || '' }));
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("manuals.selectManufacturerOptionalPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availableOptions.manufacturers.map(mfg => (
                    <SelectItem key={mfg} value={mfg}>{mfg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkEditDialog(false);
                  setBulkEditForm({ languages: [], category: '', subcategory: '', manufacturer: '' });
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={async () => {
                  if (selectedManuals.size === 0) return;
                  
                  const manualIds = Array.from(selectedManuals);
                  
                  // Update all selected manuals
                  try {
                    for (const id of manualIds) {
                      const manual = manualsQuery.data?.manuals.find(m => m.id === id);
                      if (!manual) continue;
                      
                      const updates: Record<string, any> = {};
                      
                      // Languages: merge with existing languages (add new ones, keep existing)
                      if (bulkEditForm.languages.length > 0) {
                        const existingLanguages = manual.languages || [];
                        const mergedLanguages = Array.from(new Set([...existingLanguages, ...bulkEditForm.languages]));
                        updates.languages = mergedLanguages;
                      }
                      
                      // Category, subcategory, manufacturer: only update if set
                      if (bulkEditForm.category) {
                        updates.category = bulkEditForm.category;
                      }
                      if (bulkEditForm.subcategory) {
                        updates.subcategory = bulkEditForm.subcategory;
                      }
                      if (bulkEditForm.manufacturer) {
                        updates.manufacturer = bulkEditForm.manufacturer;
                      }
                      
                      // Only update if there are changes
                      if (Object.keys(updates).length > 0) {
                        await updateMutation.mutateAsync({ id, data: updates });
                      }
                    }
                    setShowBulkEditDialog(false);
                    setSelectedManuals(new Set());
                    setBulkEditForm({ languages: [], category: '', subcategory: '', manufacturer: '' });
                  } catch (error) {
                    console.error('Bulk update error:', error);
                    alert(t("manualsExtra.bulkUpdateFailed"));
                  }
                }}
                disabled={selectedManuals.size === 0 || updateMutation.isPending}
              >
                {updateMutation.isPending ? t("common.modifying") : t("manualsExtra.modifyCount", { count: selectedManuals.size })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog – mindig renderelve Admin esetén (mobil FAB működéshez) */}
      {user?.role === 'Admin' && (
        <Sheet open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <SheetContent 
            side={isMobile ? "bottom" : "right"}
            className={cn(
              isMobile 
                ? "rounded-t-2xl pb-[env(safe-area-inset-bottom)] max-h-[85dvh]" 
                : "sm:max-w-2xl overflow-y-auto",
              "flex flex-col p-0"
            )}
            hideClose={true}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Compact header: Title + Close (single row) */}
            <div className={cn(
              "flex items-center justify-between flex-shrink-0 px-6",
              isMobile ? "pt-3 pb-2" : "py-4 border-b"
            )}>
              <h2 className="text-lg font-semibold">{t('manuals.uploadTitle')}</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setUploadDialogOpen(false)}
                className="flex-shrink-0 h-8 w-8 rounded-full"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t('common.close')}</span>
              </Button>
            </div>
            
            <div className={cn(
              "flex-1 overflow-y-auto overscroll-contain px-6 space-y-4 min-w-0",
              isMobile ? "py-4" : "py-6"
            )}>
              <div className="min-w-0">
                <Label>{t('manualsExtra.selectFilesLabel')}</Label>
                <Input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="mt-1"
                />
              </div>

              {uploadQueue.length > 0 && (
                <div className="space-y-2 min-w-0 overflow-hidden">
                  <Label>{t('manualsExtra.selectedFilesLabel', { count: uploadQueue.length })}</Label>
                  <div className="space-y-3 max-h-60 overflow-y-auto overflow-x-hidden border rounded p-3 min-w-0">
                    {uploadQueue.map(item => (
                      <div key={item.id} className="space-y-2 min-w-0 overflow-hidden">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <span className="text-sm font-medium truncate min-w-0 flex-1">{item.file.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFromQueue(item.id)}
                            className="h-6 w-6 p-0 flex-shrink-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <Input
                          type="text"
                          placeholder={t("manualsExtra.fileNameOptionalPlaceholder", { name: item.file.name.replace(/\.[^/.]+$/, '') })}
                          value={item.name || ''}
                          onChange={(e) => updateFileName(item.id, e.target.value)}
                          className="text-sm min-w-0 max-w-full"
                        />
                        <Textarea
                          placeholder={t("manuals.typesPlaceholder")}
                          value={item.fileTypes || ''}
                          onChange={(e) => updateFileTypes(item.id, e.target.value)}
                          className="text-sm min-h-[60px] min-w-0 max-w-full resize-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="min-w-0 overflow-hidden">
                <Label>{t('manualsExtra.descriptionLabel')}</Label>
                <Textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder={t("manuals.optionalDescriptionPlaceholder")}
                  className="mt-1 min-w-0 max-w-full resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t("manualsExtra.metadataExtractedHint")}
                </p>
              </div>

              <div className="border-t pt-4 space-y-4 min-w-0 overflow-hidden">
                <div className={cn("grid gap-4 min-w-0", isMobile ? "grid-cols-1" : "grid-cols-2")}>
                  <div>
                    <Label>{t('manuals.manufacturer')}</Label>
                    <Select
                      value={uploadForm.manufacturer}
                      onValueChange={(value) => {
                        if (value === '__create_new__') {
                          setCreateManufacturerDialogOpen(true);
                        } else {
                          setUploadForm(prev => ({ ...prev, manufacturer: value }));
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t("manuals.selectManufacturerPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableOptions.manufacturers.map(mfg => (
                          <SelectItem key={mfg} value={mfg}>{mfg}</SelectItem>
                        ))}
                        <SelectItem value="__create_new__" className="text-primary font-medium">
                          <Plus className="h-4 w-4 inline mr-2" />
                          {t('manualsExtra.addManufacturerTitle')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('manuals.category')}</Label>
                    <Select
                      value={uploadForm.category}
                      onValueChange={(value) => {
                        if (value === '__create_new__') {
                          setCreateCategoryDialogOpen(true);
                        } else {
                          setUploadForm(prev => ({ ...prev, category: value, subcategory: '' }));
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t("manuals.selectCategoryPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriesQuery.data?.map(cat => (
                          <SelectItem key={cat.id} value={cat.code}>{cat.name}</SelectItem>
                        ))}
                        <SelectItem value="__create_new__" className="text-primary font-medium">
                          <Plus className="h-4 w-4 inline mr-2" />
                          {t('manualsExtra.addCategoryTitle')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('manualsExtra.subcategoryLabel')}</Label>
                    <Select
                      value={uploadForm.subcategory}
                      onValueChange={(value) => {
                        if (value === '__create_new__') {
                          setCreateSubcategoryDialogOpen(true);
                        } else {
                          setUploadForm(prev => ({ ...prev, subcategory: value }));
                        }
                      }}
                      disabled={!uploadForm.category}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={uploadForm.category ? t('manualsExtra.selectSubcategoryPlaceholder') : t('manualsExtra.selectCategoryFirst')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const category = categoriesQuery.data?.find(c => c.code === uploadForm.category);
                          if (!category) return null;
                          const subs = subcategoriesUploadQuery.data || [];
                          return (
                            <>
                              {subs.map(sub => (
                                <SelectItem key={sub.id} value={sub.code}>{sub.name}</SelectItem>
                              ))}
                              <SelectItem value="__create_new__" className="text-primary font-medium">
                                <Plus className="h-4 w-4 inline mr-2" />
                                {t('manualsExtra.addSubcategoryTitle')}
                              </SelectItem>
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('manualsExtra.languagesLabel')}</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {availableOptions.languages.length > 0 ? (
                        availableOptions.languages.map(lang => {
                          const isSelected = uploadForm.languages.includes(lang);
                          return (
                            <button
                              key={lang}
                              type="button"
                              onClick={() => {
                                const newLangs = isSelected
                                  ? uploadForm.languages.filter(l => l !== lang)
                                  : [...uploadForm.languages, lang];
                                setUploadForm(prev => ({ ...prev, languages: newLangs }));
                              }}
                              className={cn(
                                "px-3 py-1 rounded-md border text-sm flex items-center gap-2 transition-colors",
                                isSelected ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"
                              )}
                            >
                              <span className="text-lg">{LANGUAGE_FLAGS[lang] || '🌐'}</span>
                              <span>{LANGUAGE_NAMES[lang] || lang}</span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="text-sm text-muted-foreground">{t('manualsExtra.noLanguagesAvailable')}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleUpload}
                disabled={uploadQueue.length === 0 || uploadMutation.isPending}
                className={cn("w-full", isMobile && "min-h-[48px]")}
              >
                {uploadMutation.isPending ? t('manuals.uploading') : t('manuals.upload')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Mobil: szűrő panel (Sheet) – keresés, kategória, gyártó, nyelv */}
      {isMobile && (
        <AppSheet
          open={filterSheetOpen}
          onOpenChange={setFilterSheetOpen}
          title={t("common.filterLabel")}
          headerActions={
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground text-xs"
              onClick={() => {
                setFilters({ manufacturers: new Set(), languages: [], category: undefined, subcategory: undefined });
                setFilterSheetOpen(false);
              }}
            >
              {t("manualsExtra.clearFilters")}
            </Button>
          }
        >
          <ManualsSidebar
            searchQuery={searchQuery}
            onSearchChange={(q) => { handleSearch(q); setFilterSheetOpen(false); }}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            searchHistory={searchHistoryQuery.data}
            onSearchHistorySelect={(term) => { handleSearch(term); setFilterSheetOpen(false); }}
            categories={categoriesQuery.data || []}
            subcategories={allSubcategoriesQuery.data || []}
            manufacturers={manufacturersQuery.data || []}
            languages={availableOptions.languages}
            categoryCounts={categoryCountsQuery.data || {}}
            isLoadingCategories={categoriesQuery.isLoading}
            isLoadingManufacturers={manufacturersQuery.isLoading}
            compact
          />
        </AppSheet>
      )}

      {/* Mobil: feltöltés gomb jobb alsó sarokban (CRM FAB stílus) */}
      {isMobile && user?.role === 'Admin' && !selectionModeEnabled && (
        <>
          {/* fab-token: unified D */}
          <FabMenu
            alwaysShowMenu
            fabAriaLabel={t("manuals.upload")}
            actions={[
              {
                id: "upload",
                label: t("manuals.upload"),
                icon: <Upload className="h-5 w-5" />,
                onClick: () => setUploadDialogOpen(true),
              },
            ]}
          />
        </>
      )}

      {isMobile && selectionModeEnabled && canBulkSelect && (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
              <Button
                ref={bulkHeaderDismissRef}
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                aria-label={t("manuals.selectionOff")}
                onClick={() => {
                  hapticTap(12);
                  exitManualSelectionMode();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
              <SelectionLiveRegion count={selectedManuals.size} />
              <span className="max-w-[10rem] truncate text-sm text-muted-foreground sm:max-w-none">
                {selectedManuals.size} {t("images.selected")}
              </span>
              {selectedManuals.size === 0 ? (
                <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
              ) : null}
              <button
                type="button"
                className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50 hidden sm:inline"
                disabled={visibleManuals.length === 0}
                onClick={() => setSelectedManuals(new Set(visibleManuals.map((m) => m.id)))}
              >
                {t("common.selectAll")}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 w-9 shrink-0 p-0 ml-auto" aria-label={t("common.more")}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setSelectedManuals(new Set(visibleManuals.map((m) => m.id)))}
                    disabled={visibleManuals.length === 0}
                  >
                    {t("common.selectAll")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setShowBulkEditDialog(true)}
                    disabled={selectedManuals.size === 0}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {t("manuals.editBulk")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>
      )}
      {inAppPdfViewer ? (
        <InAppPdfGateBoundary
          pdfData={inAppPdfViewer.data}
          t={t}
          onDismiss={() => setInAppPdfViewer(null)}
        >
          <Suspense
            fallback={
              <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80"
                role="status"
                aria-live="polite"
              >
                <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
              </div>
            }
          >
            <ManualPdfViewerDialog
              open
              onOpenChange={(o) => {
                if (!o) setInAppPdfViewer(null);
              }}
              pdfData={inAppPdfViewer.data}
              title={inAppPdfViewer.title}
            />
          </Suspense>
        </InAppPdfGateBoundary>
      ) : null}
    </div>
  );
}
